import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { CsvCell, downloadCsv, toFilenamePart } from '../../utils/csvUtils';
import '../shared.css';
import './AuditLog.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface KGOption   { kg_id: string }
interface VerOption  { version_id: string; published: boolean }
interface InstOption { instance_id: string; default_instance: boolean }

interface QueryEntry {
  query_id:     string;
  status:       string;
  query_string: string;
  query_type:   string;
  timestamp:    number;
  long_running: boolean;
  instance_id?: string;
  user_id?:     string;
}

type SortCol = 'timestamp' | 'query_type';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  COMPLETED:   '#24CF35',
  completed:   '#24CF35',
  done:        '#24CF35',
  RUNNING:     '#F78E12',
  running:     '#F78E12',
  IN_PROGRESS: '#F78E12',
  in_progress: '#F78E12',
  QUEUED:      '#3C5DE2',
  queued:      '#3C5DE2',
  FAILED:      '#FF004D',
  failed:      '#FF004D',
  CANCELLED:   '#9CA3AF',
  cancelled:   '#9CA3AF',
  TIMED_OUT:   '#F59E0B',
  timed_out:   '#F59E0B',
  error:       '#FF004D',
};

function statusColor(s: string) {
  return STATUS_COLORS[s?.toLowerCase()] ?? '#9CA3AF';
}

function statusIsError(s: string) {
  return ['FAILED', 'failed', 'TIMED_OUT', 'timed_out', 'error'].includes(s);
}

function defaultFrom() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}
function defaultTo() {
  return new Date().toISOString().slice(0, 10);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function relTime(ms: number): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  const s = Math.floor(diff / 1000);
  if (s < 60)   return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)   return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h ago`;
  const day = Math.floor(h / 24);
  return `${day}d ago`;
}

function absTime(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString([], {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short',
  });
}

// Minimal Cypher keyword highlight — wraps keywords in a <mark> span
function highlightCypher(text: string): React.ReactNode {
  const keywords = /\b(MATCH|WHERE|RETURN|WITH|CREATE|MERGE|DELETE|SET|LIMIT|SKIP|ORDER BY|OPTIONAL|UNWIND|CALL|YIELD|AS|AND|OR|NOT|IN|IS|NULL|COUNT|DISTINCT|COLLECT)\b/gi;
  const parts = text.split(keywords);
  return parts.map((part, i) =>
    keywords.test(part)
      ? <mark key={i} className="al-kw">{part}</mark>
      : part
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatusDot({ status, size = 8 }: { status: string; size?: number }) {
  return (
    <span
      className="al-status-dot"
      style={{ background: statusColor(status), width: size, height: size }}
      title={status}
    />
  );
}

function TypeChip({ type }: { type: string }) {
  return <span className="al-type-chip">{type || 'CYPHER'}</span>;
}

const RESULTS_CLIP = 5;

function renderCell(val: any): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

function extractResults(raw: any): { rows: any[]; executionTime: string | null; rowCount: number | null } {
  try {
    if (!raw) return { rows: [], executionTime: null, rowCount: null };
    const attrs = raw?.data?.attributes ?? raw?.attributes ?? null;
    const executionTime = attrs?.execution_time ?? attrs?.executionTime ?? attrs?.duration_ms ?? null;
    const rowCount = attrs?.row_count ?? attrs?.rowCount ?? null;
    let rows: any[] = [];
    if (Array.isArray(raw)) {
      rows = raw;
    } else {
      const src = attrs ?? raw?.data ?? raw;
      const r = src?.result ?? src?.results ?? src?.rows ?? src?.data;
      if (Array.isArray(r)) rows = r;
      else if (r && typeof r === 'object') {
        const first = Object.values(r).find(v => Array.isArray(v));
        if (first) rows = first as any[];
      }
    }
    return { rows, executionTime, rowCount };
  } catch {
    return { rows: [], executionTime: null, rowCount: null };
  }
}

// Expanded row detail panel — fetches results lazily on mount
function RowDetail({ q, kgId, versionId, userName, onClose }: {
  q: QueryEntry; kgId: string; versionId: string; userName?: string; onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [resLoading, setResLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [rawResponse, setRawResponse] = useState<any>(null);
  const [executionTime, setExecutionTime] = useState<string | null>(null);
  const [resRowCount, setResRowCount] = useState<number | null>(null);
  const [resError, setResError] = useState('');
  const [resErrorDetail, setResErrorDetail] = useState('');
  const [showAllRows, setShowAllRows] = useState(false);

  useEffect(() => {
    if (!kgId || !versionId || !q.query_id) return;
    let cancelled = false;
    setResLoading(true);
    setResError('');
    setResErrorDetail('');
    setRawResponse(null);
    apiService.getQueryResults(kgId, versionId, q.query_id)
      .then((res: any) => {
        if (cancelled) return;
        const { rows: r, executionTime: et, rowCount: rc } = extractResults(res);
        setRows(r);
        setExecutionTime(et != null ? String(et) : null);
        setResRowCount(rc);
        // If we couldn't parse structured rows, show the raw response
        if (r.length === 0 && res != null) setRawResponse(res);
      })
      .catch((err: any) => {
        if (cancelled) return;
        const msg = extractErrorMessage(err, 'Could not load results for this query.');
        // Also capture the full error response body for extra detail
        const body = err?.response?.data;
        setResError(msg);
        if (body && typeof body === 'object') setResErrorDetail(JSON.stringify(body, null, 2));
      })
      .finally(() => { if (!cancelled) setResLoading(false); });
    return () => { cancelled = true; };
  }, [q.query_id, kgId, versionId]);

  function copy() {
    navigator.clipboard.writeText(q.query_id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  const cols = rows.length > 0 ? Object.keys(rows[0]) : [];
  const visibleRows = showAllRows ? rows : rows.slice(0, RESULTS_CLIP);
  const hiddenCount = rows.length - RESULTS_CLIP;

  return (
    <div className="al-row-detail">
      <div className="al-detail-header">
        <span className="al-detail-title">Query detail</span>
        <button className="al-detail-close" onClick={onClose} aria-label="Close">×</button>
      </div>

      {/* Query text */}
      <pre className="al-detail-query">{q.query_string}</pre>

      {/* Metadata strip */}
      <div className="al-detail-meta">
        <div className="al-detail-field">
          <span className="al-detail-key">Query ID</span>
          <span className="al-detail-val al-mono">{q.query_id}</span>
          <button className="al-detail-copy" onClick={copy}>{copied ? '✓ Copied' : 'Copy'}</button>
        </div>
        {q.instance_id && (
          <div className="al-detail-field">
            <span className="al-detail-key">Instance</span>
            <span className="al-detail-val al-mono">{q.instance_id}</span>
          </div>
        )}
        {q.user_id && (
          <div className="al-detail-field">
            <span className="al-detail-key">User</span>
            <span className="al-detail-val">{q.user_id}</span>
          </div>
        )}
        {userName && (
          <div className="al-detail-field">
            <span className="al-detail-key">User name</span>
            <span className="al-detail-val">{userName}</span>
          </div>
        )}
        <div className="al-detail-field">
          <span className="al-detail-key">Timestamp</span>
          <span className="al-detail-val al-mono">{absTime(q.timestamp)}</span>
        </div>
        <div className="al-detail-field">
          <span className="al-detail-key">Status</span>
          <span className="al-detail-val" style={{ color: statusColor(q.status), fontWeight: 600 }}>{q.status}</span>
        </div>
        <div className="al-detail-field">
          <span className="al-detail-key">Long running</span>
          <span className="al-detail-val">{q.long_running ? 'Yes' : 'No'}</span>
        </div>
        {executionTime != null && (
          <div className="al-detail-field">
            <span className="al-detail-key">Exec time</span>
            <span className="al-detail-val al-mono">{executionTime}ms</span>
          </div>
        )}
      </div>

      {/* Results section */}
      <div className="al-detail-results-wrap">
        <div className="al-detail-results-header">
          <span className="al-detail-results-title">
            Response
            {resLoading && (
              <svg className="al-detail-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                style={{ animation: 'al-spin 1s linear infinite' }}>
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            )}
          </span>
          {!resLoading && rows.length > 0 && (
            <span className="al-detail-results-count">
              {resRowCount ?? rows.length} row{(resRowCount ?? rows.length) !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {resError ? (
          <div className="al-detail-res-error-wrap">
            <span className="al-detail-res-error-msg">{resError}</span>
            {resErrorDetail && (
              <pre className="al-detail-res-raw">{resErrorDetail}</pre>
            )}
          </div>
        ) : resLoading ? (
          <span className="al-detail-res-empty al-detail-res-loading">Loading…</span>
        ) : rows.length > 0 ? (
          <>
            <div className="al-detail-results-table-wrap">
              <table className="al-detail-results-table">
                <thead>
                  <tr>{cols.map(c => <th key={c}>{c}</th>)}</tr>
                </thead>
                <tbody>
                  {visibleRows.map((row, i) => (
                    <tr key={i}>
                      {cols.map(c => <td key={c} title={renderCell(row[c])}>{renderCell(row[c])}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!showAllRows && hiddenCount > 0 && (
              <button className="al-detail-show-more" onClick={() => setShowAllRows(true)}>
                Show {hiddenCount} more row{hiddenCount !== 1 ? 's' : ''}
              </button>
            )}
          </>
        ) : rawResponse != null ? (
          <pre className="al-detail-res-raw">{JSON.stringify(rawResponse, null, 2)}</pre>
        ) : (
          <span className="al-detail-res-empty">No response data.</span>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const TABLE_PAGE = 100;

const AuditLog: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Pre-selections coming from URL (e.g. deep-link from InstanceList)
  const initKg   = searchParams.get('kg')       ?? '';
  const initVer  = searchParams.get('version')  ?? '';
  const initInst = searchParams.get('instance') ?? '';

  // ── Selector state ───────────────────────────────────────────────────────────
  const [kgs,       setKgs]       = useState<KGOption[]>([]);
  const [versions,  setVersions]  = useState<VerOption[]>([]);
  const [instances, setInstances] = useState<InstOption[]>([]);

  const [selKg,   setSelKg]   = useState(initKg);
  const [selVer,  setSelVer]  = useState(initVer);
  const [selInst, setSelInst] = useState(initInst);

  const [loadingKgs,  setLoadingKgs]  = useState(true);
  const [loadingVers, setLoadingVers] = useState(false);
  const [loadingInst, setLoadingInst] = useState(false);

  // ── Date range ───────────────────────────────────────────────────────────────
  const [dateFrom, setDateFrom] = useState(defaultFrom());
  const [dateTo,   setDateTo]   = useState(defaultTo());
  const [datePopover, setDatePopover] = useState(false);
  const datePopoverRef = useRef<HTMLDivElement>(null);

  // ── Query state ──────────────────────────────────────────────────────────────
  const [queries,      setQueries]      = useState<QueryEntry[]>([]);
  const [loading,      setLoading]      = useState(false);
  const [loadedCount,  setLoadedCount]  = useState(0);
  const [error,        setError]        = useState('');
  const [lastFetch,    setLastFetch]    = useState<Date | null>(null);
  const [autoRefresh,  setAutoRefresh]  = useState(false);

  // ── Filter / sort ────────────────────────────────────────────────────────────
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('COMPLETED');
  const [sortCol,      setSortCol]      = useState<SortCol>('timestamp');
  const [sortAsc,      setSortAsc]      = useState(false);
  const [tablePage,    setTablePage]    = useState(0);
  const density = 'compact';
  const [expandedId,   setExpandedId]   = useState<string | null>(null);

  // ── User id → display name resolution (query log only carries user_id) ────────
  const [orgId,     setOrgId]     = useState(() => sessionStorage.getItem('polly_org_id') ?? '');
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const userName = useCallback(
    (uid?: string) => (uid && userNames[uid]) || '',
    [userNames],
  );

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // ── Close date popover on outside click or Escape ────────────────────────────
  useEffect(() => {
    if (!datePopover) return;
    const clickHandler = (e: MouseEvent) => {
      if (datePopoverRef.current && !datePopoverRef.current.contains(e.target as Node))
        setDatePopover(false);
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDatePopover(false);
    };
    document.addEventListener('mousedown', clickHandler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', clickHandler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [datePopover]);

  // Clear URL params after consuming them (one-time on mount)
  useEffect(() => {
    if (initKg || initVer || initInst) setSearchParams({}, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load KGs ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setLoadingKgs(true);
        const res: any = await apiService.listKGs(true);
        const seen = new Set<string>();
        const list: KGOption[] = (res.data ?? [])
          .map((item: any) => ({ kg_id: item.kg_id ?? item.id ?? '' }))
          .filter((kg: KGOption) => {
            if (!kg.kg_id || seen.has(kg.kg_id)) return false;
            seen.add(kg.kg_id);
            return true;
          });
        setKgs(list);
        // Capture org id (for user-name resolution) from the KG payload, same as Layout.
        if (!orgId) {
          const firstOrg = (res.data ?? [])
            .map((item: any) => item?.org_id ?? item?.attributes?.org_id ?? '')
            .find((x: string) => x);
          if (firstOrg) {
            setOrgId(String(firstOrg));
            sessionStorage.setItem('polly_org_id', String(firstOrg));
          }
        }
        // Use URL-provided KG if valid, otherwise default to first
        const preferred = initKg && list.some((k: KGOption) => k.kg_id === initKg) ? initKg : list[0]?.kg_id ?? '';
        if (!selKg) setSelKg(preferred);
      } catch (e: any) {
        setError(extractErrorMessage(e, 'Failed to load knowledge graphs'));
      } finally {
        setLoadingKgs(false);
      }
    })();
  }, []);

  // ── Resolve org users → id→name map (best-effort, non-blocking) ───────────────
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const users = await apiService.listOrgUsers(orgId);
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const u of users) {
          map[u.id] = `${u.firstName} ${u.lastName}`.trim() || u.email || u.id;
        }
        setUserNames(map);
      } catch { /* non-critical: table falls back to user_id / — */ }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  // ── Load published versions when KG changes ───────────────────────────────────
  useEffect(() => {
    if (!selKg) return;
    setVersions([]); setInstances([]);
    if (!initVer) setSelVer('');
    if (!initInst) setSelInst('');
    setQueries([]); setLoadedCount(0);
    (async () => {
      try {
        setLoadingVers(true);
        const res: any = await apiService.listVersions(selKg, true);
        const seenVerIds = new Set<string>();
        const list: VerOption[] = (res.data ?? [])
          .map((item: any) => ({
            version_id: String(item.version_id ?? item.id ?? ''),
            published:  item.published ?? item.attributes?.published ?? false,
          }))
          .filter((v: VerOption) => {
            if (!v.version_id || seenVerIds.has(v.version_id)) return false;
            if (!v.published) return false;
            seenVerIds.add(v.version_id);
            return true;
          })
          .sort((a: VerOption, b: VerOption) => Number(b.version_id) - Number(a.version_id));
        setVersions(list);
        const prefVer = initVer && list.some((v: VerOption) => v.version_id === initVer) ? initVer : list[0]?.version_id ?? '';
        setSelVer(prefVer);
      } catch (e: any) {
        setError(extractErrorMessage(e, 'Failed to load versions'));
      } finally {
        setLoadingVers(false);
      }
    })();
  }, [selKg]);

  // ── Load running instances when version changes ────────────────────────────────
  useEffect(() => {
    if (!selKg || !selVer) return;
    setInstances([]); setSelInst('');
    (async () => {
      try {
        setLoadingInst(true);
        const res: any = await apiService.listInstances(selKg, selVer);
        const list: InstOption[] = (res.data ?? [])
          .map((item: any) => {
            const a = item.attributes ?? item;
            return {
              instance_id:      a.instance_id      ?? item.id ?? '',
              default_instance: a.default_instance ?? false,
              status:           a.status           ?? '',
            };
          })
          .filter((i: any) => ['running', 'active'].includes(i.status.toLowerCase()));
        setInstances(list);
        const prefInst = initInst && list.some((i: any) => i.instance_id === initInst) ? initInst : list[0]?.instance_id ?? '';
        setSelInst(prefInst);
      } catch { /* non-critical */ } finally {
        setLoadingInst(false);
      }
    })();
  }, [selKg, selVer]);

  // ── Fetch — exhaust all next_token pages ──────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!selKg || !selVer) return;
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setError('');
    setQueries([]);
    setLoadedCount(0);
    setTablePage(0);
    setExpandedId(null);

    // statusFilter is already an exact API value (e.g. COMPLETED, IN_PROGRESS)
    const apiStatus = statusFilter;
    // API expects epoch seconds, not ms
    const startDate = dateFrom ? Math.floor(new Date(dateFrom).getTime() / 1000) : undefined;
    const endDate   = dateTo   ? Math.floor(new Date(dateTo + 'T23:59:59').getTime() / 1000) : undefined;

    let token: string | undefined;
    let acc: QueryEntry[] = [];

    try {
      while (true) {
        if (abortRef.current?.signal.aborted) break;
        const res: any = await apiService.listQueries(selKg, selVer, {
          pageSize: 100, nextToken: token,
          instanceId: selInst || undefined,
          status: apiStatus, startDate, endDate,
          includeUserId: true,
        });
        const raw: any[] = Array.isArray(res) ? res : (res.data ?? res.queries ?? []);
        const items: QueryEntry[] = raw.map((item: any) => {
          const a = item.attributes ?? item;
          return {
            query_id:     a.query_id     ?? item.id ?? '',
            status:       a.status       ?? '',
            query_string: a.query_string ?? '',
            query_type:   a.query_type   ?? 'CYPHER',
            timestamp:    a.timestamp    ?? 0,
            long_running: a.long_running ?? false,
            instance_id:  a.instance_id  ?? undefined,
            user_id:      a.user_id      ?? undefined,
          };
        });
        acc = [...acc, ...items];
        setQueries([...acc]);
        setLoadedCount(acc.length);
        const nt = res.meta?.next_token ?? res.next_token ?? null;
        if (!nt) break;
        token = nt;
      }
      setLastFetch(new Date());
    } catch (e: any) {
      if ((e as any)?.name !== 'AbortError')
        setError(extractErrorMessage(e, 'Failed to load query log'));
    } finally {
      setLoading(false);
    }
  }, [selKg, selVer, selInst, statusFilter, dateFrom, dateTo]);

  const cancelFetch = () => { abortRef.current?.abort(); setLoading(false); };

  // Cleanup on unmount
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  // ── Auto-refresh (only when Live is on) ──────────────────────────────────────
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!autoRefresh || !selKg || !selVer) return;
    timerRef.current = setInterval(() => fetchAll(), 30_000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoRefresh, selKg, selVer, selInst]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filtered + sorted rows ────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let rows = queries;
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter(q =>
        q.query_string.toLowerCase().includes(s) ||
        q.query_id.toLowerCase().includes(s) ||
        (q.user_id ?? '').toLowerCase().includes(s) ||
        userName(q.user_id).toLowerCase().includes(s)
      );
    }
    return [...rows].sort((a, b) => {
      const [av, bv] = sortCol === 'timestamp'
        ? [a.timestamp, b.timestamp]
        : [a.query_type, b.query_type];
      if (av < bv) return sortAsc ? -1 : 1;
      if (av > bv) return sortAsc ?  1 : -1;
      return 0;
    });
  }, [queries, search, sortCol, sortAsc, userName]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / TABLE_PAGE));
  const pageRows   = filtered.slice(tablePage * TABLE_PAGE, (tablePage + 1) * TABLE_PAGE);

  // ── Export all filtered rows (every page, current search + sort) ─────────────
  function handleDownloadCsv() {
    const headers = [
      'Query ID', 'Timestamp', 'Status', 'Type', 'Long Running',
      'KG ID', 'Version', 'Instance ID', 'User ID', 'User Name', 'Query',
    ];
    const rows: CsvCell[][] = filtered.map(q => [
      q.query_id,
      q.timestamp ? new Date(q.timestamp).toISOString() : '',
      q.status,
      q.query_type,
      q.long_running ? 'Yes' : 'No',
      selKg,
      selVer,
      q.instance_id ?? '',
      q.user_id ?? '',
      userName(q.user_id),
      q.query_string,
    ]);
    const parts = ['audit-log', selKg, `v${selVer}`, statusFilter, `${dateFrom}_to_${dateTo}`];
    downloadCsv(`${parts.map(toFilenamePart).join('_')}.csv`, headers, rows);
  }

  function toggleSort(col: SortCol) {
    if (sortCol === col) setSortAsc(v => !v);
    else { setSortCol(col); setSortAsc(false); }
    setTablePage(0);
  }

  const dateLabel = `${dateFrom} – ${dateTo}`;
  const noSelection = !selKg || !selVer;
  const selVerObj = selVer ? versions.find(v => v.version_id === selVer) : null;
  const isUnpublishedVer = selVerObj ? !selVerObj.published : false;
  const hasNoInstances = !loadingInst && instances.length === 0;
  const unpublishedNoInstance = !noSelection && isUnpublishedVer && hasNoInstances && !loading && queries.length === 0;
  const noData = !loading && queries.length === 0 && !noSelection && !unpublishedNoInstance;
  const noFiltered = !loading && queries.length > 0 && filtered.length === 0;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="al-root">

      {/* ══ Zone 1 — Context bar ══════════════════════════════════════════════ */}
      <div className="al-context-bar">

        {/* KG */}
        <div className="al-ctx-group">
          <span className="al-ctx-label">KG</span>
          <select className="al-ctx-select" value={selKg}
            onChange={e => setSelKg(e.target.value)} disabled={loadingKgs}>
            {loadingKgs
              ? <option>Loading…</option>
              : kgs.map(kg => <option key={kg.kg_id} value={kg.kg_id}>{kg.kg_id}</option>)
            }
          </select>
        </div>

        <span className="al-ctx-sep">/</span>

        {/* Version */}
        <div className="al-ctx-group">
          <span className="al-ctx-label">Version</span>
          <select className="al-ctx-select" value={selVer}
            onChange={e => setSelVer(e.target.value)} disabled={loadingVers || !selKg}>
            {loadingVers
              ? <option>Loading…</option>
              : versions.length === 0
                ? <option value="">No versions</option>
                : versions.map(v => (
                  <option key={v.version_id} value={v.version_id}>
                    v{v.version_id}
                  </option>
                ))
            }
          </select>
        </div>

        <span className="al-ctx-sep">/</span>

        {/* Instance */}
        <div className="al-ctx-group">
          <span className="al-ctx-label">Instance</span>
          <select className="al-ctx-select" value={selInst}
            onChange={e => setSelInst(e.target.value)} disabled={loadingInst || !selVer}>
            {instances.length === 0
              ? <option value="">No running instances</option>
              : instances.map(i => (
                  <option key={i.instance_id} value={i.instance_id}>
                    {i.instance_id}{i.default_instance ? ' ·default' : ''}
                  </option>
                ))
            }
          </select>
        </div>

        {/* Date range popover */}
        <div className="al-ctx-date-wrap" ref={datePopoverRef}>
          <button
            className={`al-ctx-date-btn${datePopover ? ' al-ctx-date-btn--open' : ''}`}
            onClick={() => setDatePopover(v => !v)}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {dateLabel}
          </button>
          {datePopover && (
            <div className="al-date-popover">
              <div className="al-date-popover-row">
                <label className="al-date-label">From</label>
                <input type="date" className="al-date-input" value={dateFrom} max={dateTo}
                  onChange={e => setDateFrom(e.target.value)} />
              </div>
              <div className="al-date-popover-row">
                <label className="al-date-label">To</label>
                <input type="date" className="al-date-input" value={dateTo} min={dateFrom}
                  max={new Date().toISOString().slice(0,10)}
                  onChange={e => setDateTo(e.target.value)} />
              </div>
              <div className="al-date-shortcuts">
                {[['Today',1],['7d',7],['30d',30]].map(([label, days]) => (
                  <button key={label} className="al-date-shortcut" onClick={() => {
                    const from = new Date();
                    from.setDate(from.getDate() - Number(days) + 1);
                    setDateFrom(from.toISOString().slice(0,10));
                    setDateTo(new Date().toISOString().slice(0,10));
                    setDatePopover(false);
                  }}>{label}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="al-ctx-spacer" />

        {/* Status filter */}
        <select className="al-ctx-select al-ctx-select--status" value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setTablePage(0); }}>
          <option value="COMPLETED">Completed</option>
          <option value="IN_PROGRESS">In progress</option>
          <option value="QUEUED">Queued</option>
          <option value="FAILED">Failed</option>
          <option value="TIMED_OUT">Timed out</option>
          <option value="CANCELLED">Cancelled</option>
        </select>

        {/* Search button — only explicit trigger */}
        <button className="al-ctx-search-btn" onClick={() => fetchAll()}
          disabled={loading || noSelection}>
          {loading
            ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: 'al-spin 1s linear infinite' }}>
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
            : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
          }
          {loading ? 'Loading…' : 'Search'}
        </button>

        <span className="al-ctx-updated" style={{ opacity: loading ? 0.4 : 1 }}>
          {lastFetch ? lastFetch.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short' }) : (loading ? '—:—:— —' : '')}
        </span>
      </div>

      {/* Unpublished version warning */}
      {selVer && versions.find(v => v.version_id === selVer && !v.published) && (
        <div className="al-unpublished-warn">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          Selected KG is an <strong>unpublished</strong> version.
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="tab-banner tab-banner--error">
          <span className="tab-banner-icon">⚠</span>
          <span className="tab-banner-body">{error}</span>
          <button className="tab-banner-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* ══ Zone 2 — Log stream card ══════════════════════════════════════════ */}
      <div className="al-card">

        {/* Progress bar */}
        {loading && (
          <div className="al-progress-bar-wrap">
            <div className="al-progress-bar" />
            <span className="al-progress-text">
              Loading {loadedCount > 0 ? `${loadedCount} queries` : '…'}
            </span>
            <button className="al-progress-cancel" onClick={cancelFetch}>Cancel</button>
          </div>
        )}

        {/* Filter bar */}
        <div className="al-filter-bar">
          <div className="al-search-wrap">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input className="al-search" placeholder="Search query text, ID, user…"
              value={search} onChange={e => { setSearch(e.target.value); setTablePage(0); }} />
            {search && (
              <button className="al-search-clear" onClick={() => { setSearch(''); setTablePage(0); }}>×</button>
            )}
          </div>

          <div className="al-filter-spacer" />
          <span className="al-filter-count">
            {search ? `${filtered.length} of ${queries.length}` : `${queries.length} queries`}
            {loading && ' · loading…'}
          </span>
          <button
            className="tab-btn tab-btn--ghost"
            onClick={handleDownloadCsv}
            disabled={loading || filtered.length === 0}
            title={`Download ${filtered.length} queries as CSV`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 2v8M4.5 6.5L8 10l3.5-3.5M2.5 13.5h11" />
            </svg>
            Download CSV
          </button>
        </div>

        {/* Table / states */}
        {noSelection ? (
          <div className="al-empty">
            <div className="al-empty-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <div className="al-empty-title">Select a KG and version</div>
            <div className="al-empty-body">Only published versions with running instances can be queried.</div>
          </div>
        ) : unpublishedNoInstance ? (
          <div className="al-empty">
            <div className="al-empty-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <div className="al-empty-title">KG is unpublished — no running instance</div>
            <div className="al-empty-body">This version is not yet published and has no running instances. Publish the version or launch an instance to query audit logs.</div>
          </div>
        ) : loading && queries.length === 0 ? (
          <div className="al-skeleton-wrap">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="al-skeleton-row">
                <div className="al-skel al-skel--dot" />
                <div className="al-skel al-skel--time" />
                <div className="al-skel al-skel--chip" />
                <div className="al-skel al-skel--query" style={{ width: `${40 + (i * 7) % 40}%` }} />
              </div>
            ))}
          </div>
        ) : noFiltered ? (
          <div className="al-empty">
            <div className="al-empty-icon" style={{ opacity: 0.25 }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </div>
            <div className="al-empty-title">No matches</div>
            <div className="al-empty-body">No queries match your search.</div>
            <button className="tab-btn tab-btn--ghost" onClick={() => setSearch('')}>Clear filter</button>
          </div>
        ) : noData ? (
          <div className="al-empty">
            <div className="al-empty-icon" style={{ opacity: 0.25 }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </div>
            <div className="al-empty-title">No queries in this range</div>
            <div className="al-empty-body">Widen the date range or switch instance.</div>
          </div>
        ) : (
          <div className="al-stream" data-density={density}>
            <table className="al-table">
              <thead>
                <tr>
                  <th style={{ width: 22 }}></th>{/* dot */}
                  <th style={{ width: 90 }} className="al-th-sort" onClick={() => toggleSort('timestamp')}>
                    Time {sortCol === 'timestamp' ? (sortAsc ? '↑' : '↓') : <span className="al-sort-dim">↕</span>}
                  </th>
                  <th style={{ width: 72 }} className="al-th-sort" onClick={() => toggleSort('query_type')}>
                    Type {sortCol === 'query_type' ? (sortAsc ? '↑' : '↓') : <span className="al-sort-dim">↕</span>}
                  </th>
                  <th style={{ width: 160 }}>User</th>
                  <th style={{ width: 150 }}>Name</th>
                  <th>Query</th>
                  <th style={{ width: 24 }}></th>{/* expand */}
                </tr>
              </thead>
              <tbody>
                {pageRows.map(q => {
                  const isErr    = statusIsError(q.status);
                  const isOpen   = expandedId === q.query_id;
                  return (
                    <React.Fragment key={q.query_id}>
                      <tr
                        className={`al-row${isErr ? ' al-row--error' : ''}${q.long_running ? ' al-row--warn' : ''}${isOpen ? ' al-row--open' : ''}`}
                        onClick={() => setExpandedId(isOpen ? null : q.query_id)}
                      >
                        <td className="al-td-dot">
                          <StatusDot status={q.status} />
                          {q.long_running && <span className="al-td-long-dot" title="Long running" />}
                        </td>
                        <td className="al-td-time" title={absTime(q.timestamp)}>
                          {relTime(q.timestamp)}
                        </td>
                        <td><TypeChip type={q.query_type} /></td>
                        <td className="al-td-user" title={q.user_id ?? q.instance_id ?? ''}>
                          {q.user_id ?? q.instance_id ?? '—'}
                        </td>
                        <td className="al-td-user" title={userName(q.user_id) || ''}>
                          {userName(q.user_id) || '—'}
                        </td>
                        <td className="al-td-query">
                          <span className="al-query-text">
                            {highlightCypher(q.query_string)}
                          </span>
                        </td>
                        <td className="al-td-chevron">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 150ms ease-out' }}>
                            <polyline points="9 18 15 12 9 6"/>
                          </svg>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="al-detail-row">
                          <td colSpan={7} className="al-detail-cell">
                            <RowDetail q={q} kgId={selKg} versionId={selVer} userName={userName(q.user_id)} onClose={() => setExpandedId(null)} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer — pagination */}
        {filtered.length > 0 && (
          <div className="al-footer">
            <button className="tab-btn tab-btn--ghost" disabled={tablePage === 0}
              onClick={() => setTablePage(p => p - 1)}>‹ Prev</button>
            <span className="al-footer-info">
              {tablePage * TABLE_PAGE + 1}–{Math.min((tablePage + 1) * TABLE_PAGE, filtered.length)} of {filtered.length}
              {loading && <span className="al-footer-loading"> · fetching more…</span>}
            </span>
            <button className="tab-btn tab-btn--ghost" disabled={tablePage >= totalPages - 1}
              onClick={() => setTablePage(p => p + 1)}>Next ›</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLog;
